import { Fragment } from 'react'

import { type ContainerElement, type DashboardElement, isContainer } from '../types/dashboard'

import { classNames } from '~/utils/classNames'

import { HiTrash } from 'react-icons/hi'
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels'

import { useDashboardContext } from '../context/DashboardContext/useContext'
import { DashboardComponent } from './Dashboard'
import { SlotActionButton } from './SlotActionButton'

interface DashboardElementRendererProps {
  parentElement?: ContainerElement | null
  element: DashboardElement
  index: number
}

export const DashboardElementRenderer: React.FC<DashboardElementRendererProps> = ({ parentElement, element, index }) => {
  const { isConfigurable, handleAddTile, handleRemoveTile, handleRowResize } = useDashboardContext()

  // Helper function to check if adding a column would exceed the limit
  const wouldExceedColumnLimit = (direction: 'left' | 'right') => {
    if (!parentElement) return false

    // Only check horizontal additions
    if (direction !== 'left' && direction !== 'right') return false

    // If parent is horizontal container, check its children count
    if (parentElement.direction === 'horizontal') {
      return parentElement.children.length >= 4
    }

    return false
  }

  if (isContainer(element)) {
    // TODO: CHECK OWN WHITESPACE AND RENDER GHOST TILE AT END OF CONTAINER
    // over some threshold of whitespace, render a ghost tile
    return (
      <>
        {index > 0 && isConfigurable && parentElement?.direction !== 'vertical' && (
          <PanelResizeHandle
            className={classNames(
              'outline outline-[0.25px] outline-th-content-secondary data-[resize-handle-state=drag]:outline-th-primary data-[resize-handle-state=hover]:outline-th-border',
              'my-3'
            )}
            data-panel-id={element.id}
          />
        )}
        <Panel
          order={index}
          defaultSize={element.size}
          className="relative !overflow-visible transition-all duration-75 ease-in-out"
          onResize={(size) => handleRowResize({ size, elementId: element.id, elementType: 'container' })}
          data-panel-id={element.id}
        >
          <PanelGroup direction={element.direction} className="relative !overflow-visible">
            {element.children.map((child, index) => (
              <DashboardElementRenderer parentElement={element} element={child} index={index} />
            ))}
            {isConfigurable && element?.direction === 'vertical' && (
              <div
                data-swapy-slot={element.children.at(-1)?.id + '_ghost_slot_container'}
                key={element.children.at(-1)?.id + '_ghost_slot_container'}
                className={classNames(
                  'h-full rounded-xl data-[swapy-highlighted]:outline data-[swapy-highlighted]:outline-2 data-[swapy-highlighted]:outline-th-primary'
                )}
              >
                <div
                  data-swapy-item={element.children.at(-1)?.id + '_ghost_item_container'}
                  key={element.children.at(-1)?.id + '_ghost_item_container'}
                  data-swapy-no-drag
                  className={classNames(
                    'pointer-events-none select-none',
                    isConfigurable
                      ? 'cursor-move border-th-content-secondary hover:border-th-border [&>*]:pointer-events-none [&>*]:select-none'
                      : 'border-th-border',
                    'h-full overflow-hidden rounded-xl data-[swapy-dragging]:!z-[900] data-[swapy-dragging]:!opacity-90'
                  )}
                />
              </div>
            )}
          </PanelGroup>
        </Panel>
      </>
    )
  }

  if (!parentElement) throw new Error('Parent element not found on a slot child, this should never happen')

  // TODO: SHOULD ADD GHOST TILE AT THE END HERE AND DETECT IT HERE, SHOULD HAVE NO INTERACTION

  if (element.componentType.includes('ghost') && parentElement.direction === 'vertical') {
    return <DashboardComponent componentType={element.componentType} slotId={element.id} isConfigurable={isConfigurable} />
  }

  return (
    <Fragment key={element.id}>
      {index > 0 && isConfigurable && parentElement.direction !== 'vertical' && (
        <PanelResizeHandle
          className={classNames(
            'outline outline-[0.25px] outline-th-content-secondary data-[resize-handle-state=drag]:outline-th-primary data-[resize-handle-state=hover]:outline-th-border',
            'my-3'
          )}
          data-panel-id={element.id}
        />
      )}
      <Panel
        order={index}
        defaultSize={element.size}
        className="group relative flex flex-col !overflow-visible p-2 transition-all duration-75 ease-in-out"
        onResize={(size) => handleRowResize({ size, elementId: element.id, elementType: 'slot' })}
        data-panel-id={element.id}
      >
        {isConfigurable && (
          <>
            <SlotActionButton
              position="left"
              onClick={(componentType) => {
                if (wouldExceedColumnLimit('left')) return
                handleAddTile({ elementId: element.id, newComponentType: componentType, direction: 'left' })
              }}
              disabled={wouldExceedColumnLimit('left')}
            />
            <SlotActionButton
              position="top"
              onClick={(componentType) => {
                handleAddTile({ elementId: element.id, newComponentType: componentType, direction: 'top' })
              }}
            />
          </>
        )}
        <DashboardComponent componentType={element.componentType} slotId={element.id} isConfigurable={isConfigurable} />
        {isConfigurable && (parentElement?.direction === 'vertical' || index === parentElement!.children.length - 1) && (
          <SlotActionButton
            position="right"
            onClick={(componentType) => {
              if (wouldExceedColumnLimit('right')) return
              handleAddTile({ elementId: element.id, newComponentType: componentType, direction: 'right' })
            }}
            disabled={wouldExceedColumnLimit('right')}
          />
        )}
        {isConfigurable && (
          <SlotActionButton
            position="bottom"
            onClick={(componentType) => {
              handleAddTile({ elementId: element.id, newComponentType: componentType, direction: 'bottom' })
            }}
          />
        )}
        {isConfigurable && <HiTrash className="absolute right-4 top-4 cursor-pointer" onClick={() => handleRemoveTile({ elementId: element.id })} />}
        {isConfigurable && (
          <div
            data-swapy-slot={element.id + '_ghost_slot'}
            key={element.id + '_ghost_slot'}
            className={classNames(
              'h-full rounded-xl group-hover:hidden data-[swapy-highlighted]:outline data-[swapy-highlighted]:outline-2 data-[swapy-highlighted]:outline-th-primary'
            )}
          >
            <div
              data-swapy-item={element.id + '_ghost_item'}
              key={element.id + '_ghost_item'}
              data-swapy-no-drag
              className={classNames(
                'pointer-events-none select-none',
                isConfigurable
                  ? 'cursor-move border-th-content-secondary hover:border-th-border [&>*]:pointer-events-none [&>*]:select-none'
                  : 'border-th-border',
                'h-full overflow-hidden rounded-xl data-[swapy-dragging]:!z-[900]'
              )}
            />
          </div>
        )}
      </Panel>
    </Fragment>
  )
}
